// ===========================================
// Invoices React Query Hooks
// ===========================================
// Custom hooks for invoice and payment management API operations

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  adminInvoicesApi,
  parentInvoicesApi,
  pricingPackagesApi,
  InvoiceFilters,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  RecordPaymentInput,
  UpdatePaymentInput,
  GenerateTermInvoiceInput,
  CreatePricingPackageInput,
  UpdatePricingPackageInput,
  AddProductToInvoiceInput,
  WeekInvoicePreviewInput,
  CreateInvoiceFromWeeksInput,
  AmendInvoiceInput,
  VoidInvoiceInput,
  AmendPreviewInput,
  EditPreviewInput,
  InvoiceReminderSendInput,
  PaymentCheckReason,
} from '../api/invoices.api';
import { productKeys } from './useProducts';
import { discountKeys } from './discountKeys';
import { apiErrorCode, apiErrorMessage } from '../utils/apiError';
import { creditOutcomeWarning } from '../utils/registrationCreditOutcome';

// ===========================================
// QUERY KEYS
// ===========================================

export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters?: InvoiceFilters) => [...invoiceKeys.lists(), filters] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  statistics: () => [...invoiceKeys.all, 'statistics'] as const,
  defaultDueDate: () => [...invoiceKeys.all, 'default-due-date'] as const,
  amendments: (id: string) => [...invoiceKeys.all, 'amendments', id] as const,
  reminders: (id: string) => [...invoiceKeys.all, 'reminders', id] as const,
  amendPreview: (id: string, input: AmendPreviewInput | null) =>
    [...invoiceKeys.all, 'amend-preview', id, input] as const,
  editPreview: (id: string, input: EditPreviewInput | null) =>
    [...invoiceKeys.all, 'edit-preview', id, input] as const,
  billableContext: (familyId?: string, termId?: string) =>
    [...invoiceKeys.all, 'billable-context', familyId, termId] as const,
  weekPreview: (input: WeekInvoicePreviewInput | null) =>
    [...invoiceKeys.all, 'week-preview', input] as const,
  parent: {
    all: ['parent-invoices'] as const,
    list: () => ['parent-invoices', 'list'] as const,
    detail: (id: string) => ['parent-invoices', 'detail', id] as const,
    payments: () => ['parent-invoices', 'payments'] as const,
    bankDetails: () => ['parent-invoices', 'bank-details'] as const,
    credit: () => ['parent-invoices', 'credit'] as const,
  },
};

export const pricingPackageKeys = {
  all: ['pricing-packages'] as const,
  lists: () => [...pricingPackageKeys.all, 'list'] as const,
  list: (includeInactive?: boolean) =>
    [...pricingPackageKeys.lists(), { includeInactive }] as const,
  details: () => [...pricingPackageKeys.all, 'detail'] as const,
  detail: (id: string) => [...pricingPackageKeys.details(), id] as const,
};

// ===========================================
// ADMIN - PRICING PACKAGE QUERIES & MUTATIONS
// ===========================================

/**
 * Get all pricing packages
 */
export function usePricingPackages(includeInactive = false) {
  return useQuery({
    queryKey: pricingPackageKeys.list(includeInactive),
    queryFn: () => pricingPackagesApi.getAll(includeInactive),
  });
}

/**
 * Get a single pricing package
 */
export function usePricingPackage(id: string) {
  return useQuery({
    queryKey: pricingPackageKeys.detail(id),
    queryFn: () => pricingPackagesApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Create a new pricing package
 */
export function useCreatePricingPackage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreatePricingPackageInput) =>
      pricingPackagesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPackageKeys.all });
      enqueueSnackbar('Pricing package created successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to create pricing package', {
        variant: 'error',
      });
    },
  });
}

/**
 * Update a pricing package
 */
export function useUpdatePricingPackage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePricingPackageInput }) =>
      pricingPackagesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: pricingPackageKeys.all });
      queryClient.invalidateQueries({ queryKey: pricingPackageKeys.detail(id) });
      enqueueSnackbar('Pricing package updated successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update pricing package', {
        variant: 'error',
      });
    },
  });
}

/**
 * Delete a pricing package
 */
export function useDeletePricingPackage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => pricingPackagesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPackageKeys.all });
      enqueueSnackbar('Pricing package deleted successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to delete pricing package', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// ADMIN - INVOICE QUERIES
// ===========================================

/**
 * Get all invoices with optional filters
 */
export function useAdminInvoices(filters?: InvoiceFilters) {
  return useQuery({
    queryKey: invoiceKeys.list(filters),
    queryFn: () => adminInvoicesApi.getAll(filters),
  });
}

/**
 * Get invoice statistics for dashboard
 */
export function useInvoiceStatistics() {
  return useQuery({
    queryKey: invoiceKeys.statistics(),
    queryFn: () => adminInvoicesApi.getStatistics(),
  });
}

/**
 * The school's default due date for a new invoice (#243). `staleTime: 0` so each dialog
 * open recomputes "today"; fetched only while the dialog is open.
 */
export function useDefaultDueDate(enabled: boolean) {
  return useQuery({
    queryKey: invoiceKeys.defaultDueDate(),
    queryFn: () => adminInvoicesApi.getDefaultDueDate(),
    enabled,
    staleTime: 0,
  });
}

/**
 * Get a single invoice by ID
 */
export function useAdminInvoice(id: string) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => adminInvoicesApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Get billable context (enrollments + per-bucket weeks + matrix prices) for the
 * manual-invoice week selector. Only runs once both a family and a term are
 * chosen. Stays fresh for a few minutes — pricing/enrollments rarely change
 * mid-session, and the dialog refetches when the family/term changes.
 */
export function useBillableContext(familyId?: string, termId?: string) {
  return useQuery({
    queryKey: invoiceKeys.billableContext(familyId, termId),
    queryFn: () => adminInvoicesApi.getBillableContext(familyId!, termId!),
    enabled: !!familyId && !!termId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The SERVER's pricing of a manual week selection: the exact lines it would
 * create, priced from the pricing matrix (or a switched-on custom rate). Pass
 * null to disable. Keeps the previous preview while a new one loads, so the list
 * does not blank on every click — check `isPlaceholderData` before treating it
 * as current.
 */
export function useWeekInvoicePreview(input: WeekInvoicePreviewInput | null) {
  return useQuery({
    queryKey: invoiceKeys.weekPreview(input),
    queryFn: () => adminInvoicesApi.previewWeekSelections(input!),
    enabled: !!input,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    retry: false,
  });
}

/**
 * Live totals for the amend dialog (2026-09-28). `input` is null while the form
 * cannot be previewed (the caller debounces it). The server does ALL the money:
 * discounts, GST and caps are never recomputed in the browser.
 */
export function useAmendPreview(invoiceId: string, input: AmendPreviewInput | null) {
  return useQuery({
    queryKey: invoiceKeys.amendPreview(invoiceId, input),
    queryFn: () => adminInvoicesApi.previewAmend(invoiceId, input!),
    enabled: !!invoiceId && !!input,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
}

/**
 * Live totals for the DRAFT Edit dialog (2026-10-02) — the amend preview's twin.
 * `input` is null while the form cannot be previewed (the caller debounces it).
 */
export function useEditPreview(invoiceId: string, input: EditPreviewInput | null) {
  return useQuery({
    queryKey: invoiceKeys.editPreview(invoiceId, input),
    queryFn: () => adminInvoicesApi.previewEdit(invoiceId, input!),
    enabled: !!invoiceId && !!input,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
}

// ===========================================
// ADMIN - INVOICE MUTATIONS
// ===========================================

/**
 * Create a DRAFT invoice from a manual week selection — priced by the server.
 */
export function useCreateInvoiceFromWeeks() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateInvoiceFromWeeksInput) => adminInvoicesApi.createFromWeeks(data),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      enqueueSnackbar('Invoice created successfully', { variant: 'success' });
      // #374: a credit that did not go on, or failed after the invoice was made, is never silent.
      const warning = creditOutcomeWarning(invoice.registrationCredits);
      if (warning) enqueueSnackbar(warning, { variant: 'warning', autoHideDuration: 15000 });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to create invoice', {
        variant: 'error',
      });
    },
  });
}

/**
 * Create a new invoice
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateInvoiceInput) => adminInvoicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      enqueueSnackbar('Invoice created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to create invoice', {
        variant: 'error',
      });
    },
  });
}

/**
 * Generate term invoices for families
 */
export function useGenerateTermInvoices() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: GenerateTermInvoiceInput) =>
      adminInvoicesApi.generateTermInvoices(data),
    onSuccess: (result) => {
      // Invalidate all invoice queries to refresh the list
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });

      // Defensive checks in case result structure is unexpected
      const successCount = result?.created?.length ?? 0;
      const errorCount = result?.errors?.length ?? 0;
      // Families held back by a missing price or left to a human (#376); the dialog lists them.
      const heldCount = new Set([
        ...(result?.blocked ?? []).map((b) => b.familyId),
        ...(result?.manual ?? []).filter((m) => m.reason !== 'NOT_BILLED_NOTICE').map((m) => m.familyId),
      ]).size;

      if (errorCount === 0 && heldCount === 0 && successCount > 0) {
        enqueueSnackbar(`Successfully generated ${successCount} invoice(s)`, {
          variant: 'success',
        });
      } else if (successCount > 0) {
        const parts = [`Generated ${successCount} invoice(s)`];
        if (heldCount > 0) parts.push(`${heldCount} family/families to check`);
        if (errorCount > 0) parts.push(`${errorCount} error(s)`);
        enqueueSnackbar(parts.join(', '), { variant: 'warning' });
      }
      // Don't show snackbar if no invoices generated - dialog handles that case
    },
    onError: (error: unknown) => {
      // The server's message ("Term not found"), not axios's "Request failed with status code 404".
      enqueueSnackbar(apiErrorMessage(error, 'Failed to generate invoices'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Check first (#376): what Auto Generate would do. Creates nothing, so it invalidates nothing.
 */
export function useCheckTermInvoices() {
  return useMutation({
    mutationFn: (data: GenerateTermInvoiceInput) => adminInvoicesApi.checkTermInvoices(data),
  });
}

/**
 * Mark one "not billed" notice as handled (#376). Refreshes the invoice and the list's marker.
 */
export function useMarkNotBilledHandled() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ invoiceId, linkId }: { invoiceId: string; linkId: string }) =>
      adminInvoicesApi.markNotBilledHandled(invoiceId, linkId),
    onSuccess: (_remaining, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      enqueueSnackbar('Marked as handled', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(apiErrorMessage(error, 'Could not mark it as handled'), { variant: 'error' });
    },
  });
}

/**
 * Update an invoice
 */
export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceInput }) =>
      adminInvoicesApi.update(id, data),
    onSuccess: (invoice, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      // A line change re-derives every discount amount server-side
      // (recomputeInvoiceTotals), so the Discounts table and summary line are stale too.
      queryClient.invalidateQueries({ queryKey: discountKeys.invoiceDetails(id) });
      queryClient.invalidateQueries({ queryKey: discountKeys.audit(id) });
      // Discounts can now be applied/removed with the edit (2026-10-02): what is still
      // on offer changed, and a one-off may have been saved to the catalogue.
      queryClient.invalidateQueries({ queryKey: discountKeys.preview(id) });
      if (data.discount?.saveToCustomDiscounts) {
        queryClient.invalidateQueries({ queryKey: discountKeys.customs() });
      }
      enqueueSnackbar('Invoice updated successfully', { variant: 'success' });
      // #374: a ticked registration credit that did not go on is never silent.
      const warning = creditOutcomeWarning(invoice.registrationCredits);
      if (warning) enqueueSnackbar(warning, { variant: 'warning', autoHideDuration: 15000 });
    },
    onError: (error: Error) => {
      // The server's reason, not axios's "Request failed with status code 400".
      enqueueSnackbar(apiErrorMessage(error, 'Failed to update invoice'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Amend a SENT/OVERDUE invoice in place (2026-09-26).
 *
 * No `onError` snackbar on purpose: the amend dialog renders the refusal itself
 * (a live checkout, a payment already recorded, nothing changed), because those
 * need more room than a toast and must stay visible while the admin decides.
 */
export function useAmendInvoice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AmendInvoiceInput }) => adminInvoicesApi.amend(id, data),
    onSuccess: (result, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.amendments(id) });
      // Lines changed -> every discount amount was re-derived server-side.
      queryClient.invalidateQueries({ queryKey: discountKeys.invoiceDetails(id) });
      queryClient.invalidateQueries({ queryKey: discountKeys.audit(id) });
      queryClient.invalidateQueries({ queryKey: discountKeys.preview(id) });
      if (data.discount?.saveToCustomDiscounts) {
        queryClient.invalidateQueries({ queryKey: discountKeys.customs() });
      }

      const { notification } = result;
      // The FINAL total (discounts + GST re-applied server-side) — the dialog
      // could only preview the lines, so say what the family will actually see.
      const total = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
        parseFloat(result.invoice.total)
      );
      if (notification.requested && !notification.delivered) {
        enqueueSnackbar(
          `Invoice amended — new total ${total}. The email to the family could not be delivered; you can resend it from Amendment History.`,
          { variant: 'warning' }
        );
      } else if (notification.delivered) {
        enqueueSnackbar(`Invoice amended — new total ${total}. The family has been emailed.`, { variant: 'success' });
      } else {
        enqueueSnackbar(`Invoice amended — new total ${total} (the family was not emailed).`, { variant: 'success' });
      }
    },
  });
}

/**
 * An invoice's amendment history, newest first.
 */
export function useInvoiceAmendments(id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: invoiceKeys.amendments(id),
    queryFn: () => adminInvoicesApi.getAmendments(id),
    enabled: !!id && (options.enabled ?? true),
  });
}

/**
 * The invoice's payment-reminder history + the "Send reminder now" preview (2026-09-29).
 */
export function useInvoiceReminders(id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: invoiceKeys.reminders(id),
    queryFn: () => adminInvoicesApi.getReminders(id),
    enabled: !!id && (options.enabled ?? true),
  });
}

/**
 * "Send reminder now". The caller shows the outcome; the history is ALWAYS refetched, because a
 * timed-out request may still have sent (nginx answers 504 at 60 s while the send completes).
 */
export function useSendInvoiceReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, ...input }: { invoiceId: string } & InvoiceReminderSendInput) =>
      adminInvoicesApi.sendReminder(invoiceId, input),
    onSettled: (_data, _error, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.reminders(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
    },
  });
}

/**
 * Re-send the latest amendment's notice to the family.
 */
export function useResendAmendmentNotice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ invoiceId, amendmentId }: { invoiceId: string; amendmentId: string }) =>
      adminInvoicesApi.resendAmendmentNotice(invoiceId, amendmentId),
    onSuccess: (result, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.amendments(invoiceId) });
      enqueueSnackbar(
        result.notification.delivered
          ? 'Amendment notice re-sent to the family'
          : 'The amendment notice still could not be delivered',
        { variant: result.notification.delivered ? 'success' : 'warning' }
      );
    },
    onError: (error: unknown) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to re-send the notice'), { variant: 'error' });
    },
  });
}

/**
 * Delete an invoice
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => adminInvoicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      enqueueSnackbar('Invoice deleted successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to delete invoice', {
        variant: 'error',
      });
    },
  });
}

/**
 * Send invoice to family
 */
export function useSendInvoice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => adminInvoicesApi.send(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      enqueueSnackbar('Invoice sent successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to send invoice', {
        variant: 'error',
      });
    },
  });
}

/** Follow-up refetches after a queued send-all (see useSendAllInvoices). */
export const SEND_ALL_REFETCH_DELAYS_MS = [3_000, 10_000, 30_000, 90_000];

/**
 * Send all draft invoices
 */
export function useSendAllInvoices() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => adminInvoicesApi.sendAll(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      // The send runs in the background, so the refetch above races the job
      // and usually re-reads the OLD statuses (seen on the 2026-09-09 staging
      // walkthrough: the Smith invoice was SENT 300 ms later, and the page said
      // DRAFT until a manual reload). Refetch again on a widening schedule so a
      // small batch shows its result within seconds and a full-term run keeps
      // updating for a couple of minutes without the admin reloading.
      for (const delayMs of SEND_ALL_REFETCH_DELAYS_MS) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
        }, delayMs);
      }
      // ⚠️ Say QUEUED, never "sent" (2026-09-09). The send is a background job
      // now, so at this moment nothing has been delivered — claiming otherwise
      // would be a confident falsehood about real invoices.
      enqueueSnackbar(result.message, {
        variant: result.queued > 0 ? 'success' : 'info',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to send invoices', {
        variant: 'error',
      });
    },
  });
}

/**
 * Cancel an invoice
 */
export function useCancelInvoice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & VoidInvoiceInput) => adminInvoicesApi.cancel(id, data),
    onSuccess: (result, { id }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      // The void stands whether or not the optional email landed; say which.
      const n = result.notification;
      if (n?.requested && !n.delivered) {
        enqueueSnackbar('Invoice voided, but the email to the family could not be delivered', {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar(n?.requested ? 'Invoice voided. The family was emailed.' : 'Invoice voided', {
          variant: 'success',
        });
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to void invoice', {
        variant: 'error',
      });
    },
  });
}

/**
 * Record a manual payment
 */
export function useRecordPayment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecordPaymentInput }) =>
      adminInvoicesApi.recordPayment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
      enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      // #642: the dialog answers this one itself (it opens the Credit / Refund due step).
      if (apiErrorCode(error) === 'OVERPAYMENT_NEEDS_CHOICE') return;
      enqueueSnackbar(apiErrorMessage(error, 'Failed to record payment'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Amend a manually recorded payment. The invoice's paid total and status are
 * re-derived server-side, so every invoice view is invalidated.
 */
export function useUpdatePayment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      paymentId,
      data,
    }: {
      paymentId: string;
      invoiceId: string;
      data: UpdatePaymentInput;
    }) => adminInvoicesApi.updatePayment(paymentId, data),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
      enqueueSnackbar('Payment updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update payment', {
        variant: 'error',
      });
    },
  });
}

/**
 * Void a manually recorded payment (#642 D4: payments are never deleted). The dialog shows the
 * server's refusal (e.g. CREDIT_IN_USE, which names the invoice) itself, so no error snackbar.
 */
export function useVoidPayment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: string; invoiceId: string; reason: string }) =>
      adminInvoicesApi.voidPayment(paymentId, reason),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
      enqueueSnackbar('Payment voided', { variant: 'success' });
    },
  });
}

/** #642 Phase 2 Request 7: the owner's open "Needs checking" flags (queried only for the owner). */
export function usePaymentsNeedingCheck(enabled: boolean) {
  return useQuery({
    queryKey: [...invoiceKeys.all, 'needs-checking'] as const,
    queryFn: () => adminInvoicesApi.getPaymentsNeedingCheck(),
    enabled,
  });
}

/** #642 Phase 2 Request 7: set or resolve "Needs checking" (owner only). Refreshes every invoice read. */
export function useFlagPaymentNeedsChecking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ paymentId, reason, note }: { paymentId: string; reason: PaymentCheckReason; note?: string | null }) =>
      adminInvoicesApi.flagPaymentNeedsChecking(paymentId, { reason, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      enqueueSnackbar('Marked as needing checking', { variant: 'success' });
    },
  });
}

export function useResolvePaymentCheck() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ paymentId, note, flaggedAt }: { paymentId: string; note: string; flaggedAt?: string | null }) =>
      adminInvoicesApi.resolvePaymentCheck(paymentId, note, flaggedAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      enqueueSnackbar('Resolved', { variant: 'success' });
    },
  });
}

/**
 * Add a catalogue product to a DRAFT invoice.
 *
 * ⚠️ Invalidates productKeys.all as well as the invoice keys: an inventory-tracked
 * product has had its stock deducted server-side, so any product list on screen is
 * stale the moment this resolves.
 */
export function useAddProductToInvoice() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AddProductToInvoiceInput }) =>
      adminInvoicesApi.addProduct(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // A line change re-derives every discount amount server-side
      // (recomputeInvoiceTotals), so the Discounts table and summary line are stale too.
      queryClient.invalidateQueries({ queryKey: discountKeys.invoiceDetails(id) });
      queryClient.invalidateQueries({ queryKey: discountKeys.audit(id) });
      enqueueSnackbar('Product added to invoice', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to add product to invoice', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// PARENT - INVOICE QUERIES & MUTATIONS
// ===========================================

/**
 * Get all invoices for parent's family
 */
export function useParentInvoices(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: invoiceKeys.parent.list(),
    queryFn: () => parentInvoicesApi.getAll(),
    enabled: options?.enabled,
  });
}

/**
 * Get count of unpaid invoices for parent's family (for navigation badge)
 * Returns count of invoices with status SENT, PARTIALLY_PAID, or OVERDUE
 */
export function useParentUnpaidInvoiceCount(options?: { enabled?: boolean }) {
  const { data: invoices = [], isLoading } = useParentInvoices(options);

  const unpaidCount = invoices.filter(
    (invoice) =>
      invoice.status === 'SENT' ||
      invoice.status === 'PARTIALLY_PAID' ||
      invoice.status === 'OVERDUE'
  ).length;

  return { count: unpaidCount, isLoading };
}

/**
 * Get a single invoice for parent
 */
export function useParentInvoice(id: string) {
  return useQuery({
    queryKey: invoiceKeys.parent.detail(id),
    queryFn: () => parentInvoicesApi.getById(id),
    enabled: !!id,
  });
}

/**
 * #642 Phase 2: the parent's own family account credit. Under `parent.all`, so every parent
 * invoice refresh (a payment, a return from Checkout) refreshes it too.
 */
export function useParentFamilyCredit(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: invoiceKeys.parent.credit(),
    queryFn: () => parentInvoicesApi.getCredit(),
    enabled: options?.enabled,
  });
}

/**
 * Get payment history for parent
 */
export function useParentPaymentHistory() {
  return useQuery({
    queryKey: invoiceKeys.parent.payments(),
    queryFn: () => parentInvoicesApi.getPaymentHistory(),
  });
}

/**
 * Create a Stripe checkout session for invoice payment
 */
export function useCreatePaymentSession() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      id,
      successUrl,
      cancelUrl,
    }: {
      id: string;
      successUrl: string;
      cancelUrl: string;
    }) => parentInvoicesApi.createPaymentSession(id, successUrl, cancelUrl),
    onSuccess: (result) => {
      // Redirect to Stripe checkout
      window.location.href = result.url;
    },
    onError: (error: unknown) => {
      // The server's reason, not axios's status line — e.g. "A payment is
      // already being set up for this invoice" (CHECKOUT_IN_PROGRESS).
      enqueueSnackbar(apiErrorMessage(error, 'Failed to start payment'), {
        variant: 'error',
      });
    },
  });
}

/**
 * The parent returned from Stripe without paying: release the invoice's
 * checkout lock now. Silent and best-effort — it also lifts at expiry.
 */
export function useCancelInvoiceCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => parentInvoicesApi.cancelCheckout(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.parent.all });
    },
  });
}

/**
 * Download invoice as PDF
 */
export function useDownloadInvoicePdf() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, invoiceNumber }: { id: string; invoiceNumber: string }) =>
      parentInvoicesApi.downloadPdf(id, invoiceNumber),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to download invoice', {
        variant: 'error',
      });
    },
  });
}

/**
 * Get bank transfer details for the school
 */
export function useBankTransferDetails() {
  return useQuery({
    queryKey: invoiceKeys.parent.bankDetails(),
    queryFn: () => parentInvoicesApi.getBankDetails(),
  });
}

/**
 * Verify and process a Stripe checkout session for invoice payment
 */
export function useVerifyInvoicePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => parentInvoicesApi.verifyPayment(sessionId),
    // Never retried: each attempt can cost a Stripe request (#273).
    retry: false,
    onSuccess: () => {
      // Invalidate invoice queries to refresh the list with updated status
      queryClient.invalidateQueries({ queryKey: invoiceKeys.parent.all });
    },
  });
}

// ===========================================
// LATE FEES (#372 plan A6.2)
// ===========================================
// Each invalidates the admin AND the parent invoice caches: a waive changes what the family owes.
// Errors are shown inline by the dialogs (no snackbar, so nothing is said twice).

export function useWaiveLateFee() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, feeId, reason }: { id: string; feeId: string; reason: string }) =>
      adminInvoicesApi.waiveLateFee(id, feeId, reason),
    onSuccess: (result) => {
      const n = result.notification;
      if (n?.requested && !n.delivered) {
        enqueueSnackbar('Late fee waived, but the email to the family could not be delivered', { variant: 'warning' });
      } else {
        enqueueSnackbar(n?.requested ? 'Late fee waived. The family was emailed.' : 'Late fee waived', {
          variant: 'success',
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.parent.all });
    },
  });
}

export function useHoldReminders() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => adminInvoicesApi.holdReminders(id, note),
    onSuccess: () => enqueueSnackbar('Reminders and late fees are on hold for this invoice', { variant: 'success' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.parent.all });
    },
  });
}

export function useReleaseReminders() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => adminInvoicesApi.releaseReminders(id),
    onSuccess: () => enqueueSnackbar('Reminders and late fees resume from the next run', { variant: 'success' }),
    onError: (error: unknown) => enqueueSnackbar(apiErrorMessage(error, 'Could not release the hold'), { variant: 'error' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.parent.all });
    },
  });
}
